import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';

function LoginForm() {
  // Original login states
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [companyCode, setCompanyCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // MFA states
  const [mfaEmail, setMfaEmail] = useState('');
  const [code, setCode] = useState('');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [showCodeInput, setShowCodeInput] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isEmailVerified, setIsEmailVerified] = useState(false);

  const navigate = useNavigate();

  // MFA functions
  const handleMfaCapture = async () => {
    if (!captchaToken) {
      setError('Please complete the CAPTCHA verification');
      return;
    }

    if (!mfaEmail) {
      setError('Please enter your email address');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://api.insightos.co.uk/admin/mfacapture', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: mfaEmail,
          captchaToken: captchaToken
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Verification code sent to your email!');
        setShowCodeInput(true);
      } else {
        setError(data.message || 'Failed to send verification code');
      }
    } catch (error) {
      console.error('Error sending code:', error);
      setError('An error occurred while sending the verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleEmailCapture = async (e) => {
    e.preventDefault();
    
    if (!code) {
      setError('Please enter the verification code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch('https://api.insightos.co.uk/admin/emailcapture', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          email: mfaEmail,
          code: code
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess('Email verification successful!');
        setIsEmailVerified(true);
        // Pre-fill the email field in the login form
        setEmail(mfaEmail);
      } else {
        setError(data.message || 'Invalid verification code');
      }
    } catch (error) {
      console.error('Error verifying code:', error);
      setError('An error occurred while verifying the code');
    } finally {
      setLoading(false);
    }
  };

  // Original login function
  const handleSubmit = async (event) => {
    event.preventDefault();
    setErrorMessage('');
  
    try {
      const response = await fetch('https://api.insightos.co.uk/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',  // <-- ADD THIS LINE
        body: JSON.stringify({
          email,
          password,
          pin: companyCode,
        }),
      });
  
      const data = await response.json();
  
      if (data.success) {
        // it will now send back access if sales send to /salesdashboard, dashboard otherwise     return res.json({ success: true, message: 'Login successful', access: admin.Access, analyticsToken, analyticsTtl });

        if ((data.access || '').toLowerCase() === 'sales') {
          navigate('/sales-dashboard');
        } else {
          navigate('/dashboard');
        }
        window.location.reload();
      } else {
        setErrorMessage('Login failed: Incorrect email, password, or company code.');
      }
    } catch (error) {
      console.error('Login error:', error);
      setErrorMessage('An error occurred while logging in. Please try again.');
    }
  };
  

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 flex items-center justify-center px-4 sm:px-6 lg:px-8 bg-cover bg-center relative"
      style={{ backgroundImage: "url('/background.png')" }}
    >
      <div className="absolute inset-0 bg-indigo-800/60" />
      <div className="relative z-10 w-full">
      {!isEmailVerified ? (
        // Email MFA Verification Section
        <div className="max-w-md mx-auto w-full space-y-8">
          <div>
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
              <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
              Email Verification
            </h2>
            <p className="mt-2 text-center text-sm text-blue-200">
              {!showCodeInput 
                ? 'Enter your email to receive a verification code'
                : 'Enter the code sent to your email'
              }
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-lg rounded-lg shadow-xl p-8 border border-white/20">
            {error && (
              <div className="mb-4 bg-red-500/20 border border-red-500/50 text-red-100 px-4 py-3 rounded-md">
                {error}
              </div>
            )}
            
            {success && (
              <div className="mb-4 bg-green-500/20 border border-green-500/50 text-green-100 px-4 py-3 rounded-md">
                {success}
              </div>
            )}

            {!showCodeInput ? (
              <div className="space-y-6">
                <div>
                  <label htmlFor="mfaEmail" className="block text-sm font-medium text-white mb-2">
                    Email Address
                  </label>
                  <input
                    id="mfaEmail"
                    name="mfaEmail"
                    type="email"
                    autoComplete="email"
                    required
                    value={mfaEmail}
                    onChange={(e) => setMfaEmail(e.target.value)}
                    className="appearance-none relative block w-full px-3 py-3 border border-white/30 placeholder-gray-400 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 bg-white/5"
                    placeholder="Enter your email address"
                  />
                </div>

                <div className="flex justify-center">
                  <ReCAPTCHA
                    sitekey="6LeEdM8rAAAAANDYuqmGgLkqw9MmSv_2TzTEai6Q"
                    onChange={setCaptchaToken}
                    theme="dark"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleMfaCapture}
                  disabled={loading || !captchaToken || !mfaEmail}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending Code...
                    </div>
                  ) : (
                    'Send Verification Code'
                  )}
                </button>
              </div>
            ) : (
              <form onSubmit={handleEmailCapture} className="space-y-6">
                <div>
                  <label htmlFor="code" className="block text-sm font-medium text-white mb-2">
                    Verification Code
                  </label>
                  <input
                    id="code"
                    name="code"
                    type="text"
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    className="appearance-none relative block w-full px-3 py-3 border border-white/30 placeholder-gray-400 text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:z-10 bg-white/5 text-center text-lg tracking-widest"
                    placeholder="Enter 6-digit code"
                    maxLength="6"
                  />
                  <p className="mt-2 text-xs text-blue-200">
                    Code sent to: {mfaEmail}
                  </p>
                </div>

                <button
                  type="submit"
                  disabled={loading || !code}
                  className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Verifying...
                    </div>
                  ) : (
                    'Verify Code'
                  )}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setShowCodeInput(false);
                    setCode('');
                    setError('');
                    setSuccess('');
                  }}
                  className="w-full text-center text-sm text-blue-200 hover:text-white transition-colors duration-200"
                >
                  ← Back to email entry
                </button>
              </form>
            )}
          </div>

          <div className="text-center">
            <p className="text-xs text-blue-300">
              Secure email verification powered by reCAPTCHA
            </p>
          </div>
        </div>
      ) : (
        // Original Login Form Section
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <div className="text-center mb-8">
            <img className="mx-auto h-auto w-auto mb-4" src="/logo.png" alt="Your Company" />
            <h2 className="text-center text-2xl font-bold leading-9 tracking-tight text-white">
              Sign in to your account
            </h2>
            <p className="mt-2 text-sm text-green-200">
              ✓ Email verified: {email}
            </p>
          </div>

            <div className="bg-black/40 backdrop-blur-lg rounded-lg shadow-xl p-8 border border-white/20">
              <form className="space-y-6" onSubmit={handleSubmit}>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-white">
                    Email address
                  </label>
                  <input
                    onChange={(e) => setEmail(e.target.value)}
                    id="email"
                    name="email"
                    type="text"
                    autoComplete="email"
                    value={email}
                    className="mt-2 block w-full rounded-md border-0 bg-white/5 py-1.5 text-white placeholder-gray-300 shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-blue-500 sm:text-sm sm:leading-6"
                  />
                </div>

                <div>
                  <label htmlFor="companyCode" className="block text-sm font-medium text-white">
                    Company Code
                  </label>
                  <input
                    value={companyCode}
                    onChange={(e) => setCompanyCode(e.target.value)}
                    id="companyCode"
                    name="companyCode"
                    autoComplete="companyCode"
                    type="number"
                    required
                    className="mt-2 block w-full rounded-md border-0 bg-white/5 py-1.5 text-white placeholder-gray-300 shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-blue-500 sm:text-sm sm:leading-6"
                  />
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-white">
                    Password
                  </label>
                  <input
                    onChange={(e) => setPassword(e.target.value)}
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    className="mt-2 block w-full rounded-md border-0 bg-white/5 py-1.5 text-white placeholder-gray-300 shadow-sm ring-1 ring-inset ring-white/10 focus:ring-2 focus:ring-blue-500 sm:text-sm sm:leading-6"
                  />
                </div>

                {errorMessage && (
                  <div className="bg-red-500/20 border border-red-500/50 text-red-100 px-4 py-3 rounded-md text-sm">
                    {errorMessage}
                  </div>
                )}

                <button
                  type="submit"
                  className="flex w-full justify-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-blue-500 focus:outline focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  Sign in
                </button>
              </form>
            </div>
        </div>
      )}
      </div>
    </div>
  );
}

export default LoginForm;
